import React, { useEffect, useRef, useState } from "react";
import { api as defaultApi } from "../../api";
import { Heatmap, ShotMap } from "../football-intelligence/FootballPitch";
import { PlayerAvatar, positionLabel } from "./LineupPitch";
import { buildSheetRows, buildSheetSections, deriveHighlight, mergeMatchFacts, participationText, pitchMatchFacts } from "./sheetModel";
import "../football-intelligence/football-intelligence.css";
import "../football-intelligence/fi-extra.css";
import "./football-app.css";

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Loads this player's row for this fixture; private PitchAPI match values for admins only. */
export function usePlayerMatchData({ api = defaultApi, fixtureId, providerFixtureId, playerId, isAdmin, analyticsAllowed = isAdmin, pitchMatchId }) {
  const [state, setState] = useState({ loading: true, row: null, pitch: null, pitchTried: false });
  useEffect(() => {
    if (!playerId || !fixtureId) return undefined;
    let alive = true;
    setState({ loading: true, row: null, pitch: null, pitchTried: false });
    const log = api
      .get("/football/player/game-log", { params: { fixture_id: fixtureId, player_id: playerId, last: 30 } })
      // Game-log rows carry the provider fixture id, not CSB's internal one.
      .then(({ data }) => (providerFixtureId ? (data?.games || []).find((g) => Number(g.fixture_id) === Number(providerFixtureId)) : null) || null)
      .catch(() => null);
    const wantPitch = !!(analyticsAllowed && pitchMatchId);
    const pitch = wantPitch
      ? api
          .get(`/api/football/intelligence/player/${encodeURIComponent(playerId)}`, { params: { namespace: "api_football", match: pitchMatchId } })
          .then(({ data }) => pitchMatchFacts(data, pitchMatchId))
          .catch(() => null)
      : Promise.resolve(null);
    Promise.all([log, pitch]).then(([row, p]) => {
      if (alive) setState({ loading: false, row, pitch: p, pitchTried: wantPitch });
    });
    return () => {
      alive = false;
    };
  }, [api, fixtureId, providerFixtureId, playerId, isAdmin, analyticsAllowed, pitchMatchId]);
  return state;
}

/**
 * Bottom sheet on phones, side panel from 900px. Modal: focus moves in, is
 * trapped, Escape/backdrop/swipe-down close it, and focus returns to the
 * tapped player. The full-profile link is rendered only when the lineup
 * carries a provider player id, so a wrong profile is never linked.
 */
export default function PlayerSheet({ selection, fixtureId, providerFixtureId = null, onClose, onNavigate, isAdmin = false, analyticsAllowed = isAdmin, pitchMatchId = null, api }) {
  const { player, team, side, ev } = selection;
  const ref = useRef(null);
  const drag = useRef(null);
  const offsetRef = useRef(0); // read at touch end: state may not have flushed after a fast flick
  const [offset, setOffset] = useState(0);
  const data = usePlayerMatchData({ api, fixtureId, providerFixtureId, playerId: player?.id, isAdmin, analyticsAllowed, pitchMatchId });
  const isStarter = (team.startXI || []).some((e) => e.player?.id === player.id && player.id !== undefined);

  useEffect(() => {
    const prior = document.activeElement;
    document.body.classList.add("fa-scroll-lock");
    const node = ref.current;
    if (node) node.focus();
    return () => {
      document.body.classList.remove("fa-scroll-lock");
      if (prior && typeof prior.focus === "function") prior.focus();
    };
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const items = [...ref.current.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null || n === ref.current);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const facts = mergeMatchFacts({ row: data.row, ev, pitch: data.pitch });
  const pitch = data.pitch;
  const rows = buildSheetRows(facts, pitch);
  const sections = buildSheetSections(pitch);
  const highlight = deriveHighlight(facts, pitch);
  const hasId = player?.id !== undefined && player?.id !== null;
  const profileHref = hasId ? `/player/${player.id}?fixture_id=${fixtureId}` : null;
  const canCompare = !!(analyticsAllowed && hasId && pitchMatchId && pitch);
  const compareHref = canCompare ? `/player/${player.id}?fixture_id=${fixtureId}&tab=statistics&compare=1` : null;
  const go = (href) => (e) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(href);
    }
  };

  const touchStart = (e) => { drag.current = e.touches[0].clientY; offsetRef.current = 0; };
  const touchMove = (e) => {
    if (drag.current === null) return;
    const dy = Math.max(0, e.touches[0].clientY - drag.current);
    offsetRef.current = dy;
    setOffset(dy);
  };
  const touchEnd = () => {
    const shouldClose = offsetRef.current > 90;
    drag.current = null;
    offsetRef.current = 0;
    setOffset(0);
    if (shouldClose) onClose();
  };

  const participation = participationText(ev, isStarter);

  return (
    <div className="fa-sheet-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="sheet-backdrop">
      <div
        className="fa-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fa-sheet-title"
        tabIndex={-1}
        ref={ref}
        onKeyDown={onKeyDown}
        style={offset ? { transform: `translateY(${offset}px)`, transition: "none" } : undefined}
      >
        <div className="fa-sheet-grip" onTouchStart={touchStart} onTouchMove={touchMove} onTouchEnd={touchEnd} style={{ padding: "16px 0 12px", height: "auto", background: "none", touchAction: "none" }}>
          <div style={{ width: 44, height: 5, borderRadius: 3, background: "rgba(255,255,255,.3)", margin: "0 auto" }} />
        </div>
        <div className="fa-sheet-body">
          <div className="fa-sheet-id">
            <PlayerAvatar player={player} side={side} large />
            <h2 id="fa-sheet-title">{player.name}</h2>
            <span className="fa-sub">{team.team?.name}{participation ? ` · ${participation}` : ""}</span>
            {facts.rating !== null && <span className="fa-chip" title={facts.source}>Rating {facts.rating.toFixed(1)} · API-Football</span>}
          </div>
          <div className="fa-facts3">
            <div><b>{positionLabel(player.pos) || "—"}</b><span>Position</span></div>
            <div><b>{player.number ?? "—"}</b><span>Shirt</span></div>
            <div><b>{team.formation || "—"}</b><span>Formation</span></div>
          </div>

          {data.loading && <div className="fa-skeleton" role="status" aria-label="Loading match statistics" style={{ height: 80 }} />}

          {pitch?.heat && (
            <section aria-label="Match heatmap">
              <div className="fa-legend" style={{ marginBottom: 6 }}><b>Heatmap</b><span className="fa-sub">Recorded action density</span></div>
              <Heatmap data={pitch.heat} />
              <p className="fa-note">Where the provider recorded this player's actions. This is not touches or continuous tracking.</p>
            </section>
          )}

          {pitch?.shotList?.length > 0 && (
            <section aria-label="Match shot map" style={{ marginTop: 14 }}>
              <div className="fa-legend" style={{ marginBottom: 6 }}>
                <b>Shot map</b>
                <span className="fa-sub">{pitch.shotList.length} {pitch.shotList.length === 1 ? "shot" : "shots"} · {pitch.shotList.filter((x) => x.outcome === "Goal").length} {pitch.shotList.filter((x) => x.outcome === "Goal").length === 1 ? "goal" : "goals"}</span>
              </div>
              <ShotMap shots={pitch.shotList} half />
            </section>
          )}

          {highlight && (
            <div className="fa-highlight">
              <span className="fa-chip">Highlights</span>
              <div>{highlight}</div>
            </div>
          )}

          {!data.loading && rows.length > 0 && (
            <section aria-label="Match statistics">
              <h3 className="fa-title">Top stats</h3>
              <div className="fa-rows">{rows.map((r) => <div key={r.label}><span>{r.label}</span><b>{r.value}</b></div>)}</div>
              {sections.map((g) => (
                <div key={g.title}>
                  <h3 className="fa-title" style={{ margin: "16px 0 0" }}>{g.title}</h3>
                  <div className="fa-rows">{g.rows.map((r) => <div key={r.key}><span>{r.label}</span><b>{r.value}</b></div>)}</div>
                </div>
              ))}
              <p className="fa-note">Source: {[...new Set([facts.source, pitch ? (pitch.sourceLabel || "PitchAPI (private research)") : ""].filter(Boolean))].join(" + ")}. Anything not listed was not recorded.</p>
            </section>
          )}
          {!data.loading && rows.length === 0 && <div className="fa-empty">No recorded match statistics for this player yet.</div>}
          {!hasId && <p className="fa-note">This player is not linked to a verified profile, so no full profile is offered.</p>}
          {analyticsAllowed && data.pitchTried && !pitch && <p className="fa-note">PitchAPI match values are unavailable for this player (no verified identity mapping or cached match).</p>}
        </div>
        <div className="fa-sheet-actions">
          <button type="button" className="fa-btn icon" onClick={onClose} aria-label="Close player details">✕</button>
          {profileHref ? (
            <a className="fa-btn primary" href={profileHref} onClick={go(profileHref)}>Full profile</a>
          ) : (
            <span className="fa-btn" aria-disabled="true" style={{ opacity: 0.5 }}>Profile unavailable</span>
          )}
          {compareHref && <a className="fa-btn" href={compareHref} onClick={go(compareHref)}>Compare</a>}
        </div>
      </div>
    </div>
  );
}
